import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { FirebaseError } from 'firebase/app';
import { httpsCallable } from 'firebase/functions';

import {
  teamSchema,
  type Team,
  type TeamId,
  type TeamProfile,
  type TeamStatus,
  type TournamentId,
  type Uid,
} from '@/core/schemas';
import { AppError, toAppError, type AppErrorKind } from '@/infrastructure/errors/AppError';
import { firestore } from '@/infrastructure/firebase/app';
import { firebaseFunctions } from '@/infrastructure/firebase/functions';
import { createConverter } from '@/infrastructure/firestore/converter';
import { firestorePaths } from '@/infrastructure/firestore/paths';

const converter = createConverter(teamSchema, 'teams');

/**
 * Live subscription to the team of one captain.
 *
 * Ownership no longer sits on the public team document (docs/SECURITY.md §6.3),
 * so the captain finds their team through the guard at `captains/{uid}` — which
 * only they and the director may read — and then follows its `teamId` to the
 * team itself. Two hops, one fact in one place, nothing that can go stale.
 */
export function subscribeMyTeam(
  tournamentId: TournamentId,
  captainUid: Uid,
  onData: (team: Team | null) => void,
  onError: (error: AppError) => void,
): () => void {
  let teamUnsub: (() => void) | null = null;
  const stopTeam = () => {
    if (teamUnsub !== null) {
      teamUnsub();
      teamUnsub = null;
    }
  };

  const captainUnsub = onSnapshot(
    doc(firestore, firestorePaths.captain(tournamentId, captainUid)),
    (claim) => {
      stopTeam();

      const teamId = claim.data()?.teamId as string | undefined;
      if (!claim.exists() || teamId === undefined) {
        onData(null);
        return;
      }

      teamUnsub = onSnapshot(
        doc(firestore, firestorePaths.team(tournamentId, teamId as TeamId)).withConverter(converter),
        (team) => {
          try {
            onData(team.exists() ? team.data() : null);
          } catch (error) {
            onError(toAppError(error));
          }
        },
        (error) => {
          onError(toAppError(error));
        },
      );
    },
    (error) => {
      onError(toAppError(error));
    },
  );

  return () => {
    stopTeam();
    captainUnsub();
  };
}

/**
 * Live subscription to every team of the tournament, ordered by name so the
 * list is stable.
 */
export function subscribeTeams(
  tournamentId: TournamentId,
  onData: (teams: Team[]) => void,
  onError: (error: AppError) => void,
): () => void {
  const teams = query(
    collection(firestore, firestorePaths.teams(tournamentId)).withConverter(converter),
    orderBy('name'),
  );

  return onSnapshot(
    teams,
    (snapshot) => {
      try {
        onData(snapshot.docs.map((document) => document.data()));
      } catch (error) {
        onError(toAppError(error));
      }
    },
    (error) => {
      onError(toAppError(error));
    },
  );
}

interface RegisterTeamRequest {
  tournamentId: string;
  profile: TeamProfile;
}

interface UpdateTeamRequest {
  tournamentId: string;
  teamId: string;
  profile: TeamProfile;
}

/**
 * Registers a team through the `registerTeam` Cloud Function.
 *
 * The function (Admin SDK) writes the public team document, the non-public
 * ownership record and both uniqueness guards in one transaction, and computes
 * the `nameKey` itself. The client never touches those documents, which is what
 * keeps the captain's UID out of the public data (§6.3) and makes a spoofed
 * `nameKey` impossible (§6.1). The owning captain is taken from the auth token,
 * so no UID is passed from here.
 */
export async function registerTeam(input: {
  tournamentId: TournamentId;
  profile: TeamProfile;
}): Promise<TeamId> {
  const call = httpsCallable<RegisterTeamRequest, { teamId: string }>(
    firebaseFunctions,
    'registerTeam',
  );

  try {
    const { data } = await call({ tournamentId: input.tournamentId, profile: input.profile });
    return data.teamId as TeamId;
  } catch (error) {
    throw translateCallableError(error);
  }
}

/**
 * Edits the captain's own team through the `updateTeamProfile` Cloud Function.
 * A rename moves the name guard server-side in the same transaction; the client
 * neither computes the `nameKey` nor juggles the guard documents.
 */
export async function updateTeamProfile(input: {
  team: Team;
  profile: TeamProfile;
}): Promise<void> {
  const call = httpsCallable<UpdateTeamRequest, { ok: boolean }>(
    firebaseFunctions,
    'updateTeamProfile',
  );

  try {
    await call({
      tournamentId: input.team.tournamentId,
      teamId: input.team.id,
      profile: input.profile,
    });
  } catch (error) {
    throw translateCallableError(error);
  }
}

/**
 * A rejected Cloud Function already carries a readable German message from the
 * server; this only maps the transport code onto an {@link AppErrorKind} and
 * preserves that message rather than replacing it with a generic one.
 */
function translateCallableError(error: unknown): AppError {
  if (error instanceof AppError) {
    return error;
  }

  if (error instanceof FirebaseError && error.code.startsWith('functions/')) {
    const kind: AppErrorKind =
      error.code === 'functions/not-found'
        ? 'not-found'
        : error.code === 'functions/unavailable'
          ? 'unavailable'
          : error.code === 'functions/permission-denied' ||
              error.code === 'functions/unauthenticated'
            ? 'permission-denied'
            : 'domain';

    return new AppError(kind, error.message, error);
  }

  return toAppError(error);
}

/**
 * Deletes a team as the tournament director, removing the team, its ownership
 * record and both uniqueness guards in one batch so name and captain slot are
 * freed together. The owning captain is read from the non-public ownership
 * record rather than the public team document.
 */
export async function deleteTeamAsDirector(input: { team: Team; actorUid: Uid }): Promise<void> {
  const { team } = input;

  try {
    const owner = await getDoc(doc(firestore, firestorePaths.teamOwner(team.tournamentId, team.id)));

    const batch = writeBatch(firestore);
    batch.delete(doc(firestore, firestorePaths.team(team.tournamentId, team.id)));
    batch.delete(doc(firestore, firestorePaths.teamName(team.tournamentId, team.nameKey)));
    batch.delete(doc(firestore, firestorePaths.teamOwner(team.tournamentId, team.id)));

    const captainUid = owner.data()?.captainUid as string | undefined;
    if (captainUid !== undefined) {
      batch.delete(doc(firestore, firestorePaths.captain(team.tournamentId, captainUid as Uid)));
    }

    await batch.commit();
  } catch (error) {
    throw toAppError(error);
  }
}

/** Sets a team's seeding position (director only, before the draw). */
export async function setTeamSeed(input: {
  team: Team;
  seed: number | null;
  actorUid: Uid;
}): Promise<void> {
  const { team, seed, actorUid } = input;

  try {
    await updateDoc(doc(firestore, firestorePaths.team(team.tournamentId, team.id)), {
      seed,
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });
  } catch (error) {
    throw toAppError(error);
  }
}

/**
 * Sets the team's participation status.
 *
 * `withdrawn` is what a team turns into once it drops out — deliberately not the
 * same as {@link deleteTeamAsDirector}, which frees the name and the captain
 * slot. A withdrawn team keeps its documents (so past results and standings
 * stay attributable) but drops out of every future phase planning, because
 * {@link toSeeds} filters it out. Matches already scheduled must be resolved by
 * the director as walkovers.
 */
export async function setTeamStatus(input: {
  team: Team;
  status: TeamStatus;
  actorUid: Uid;
}): Promise<void> {
  const { team, status, actorUid } = input;

  try {
    await updateDoc(doc(firestore, firestorePaths.team(team.tournamentId, team.id)), {
      status,
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });
  } catch (error) {
    throw toAppError(error);
  }
}
